// src/components/investor/CardActionButton.tsx
//
// Shared action button for investor CARD action rows (submission + dealflow,
// list + grid). The card analogue of ModalAction: one place that owns the
// fill-the-row layout (a `<View style={{flex:1}}>` wrapper — AnimatedPressable's
// own flex does NOT stretch, see CLAUDE.md <pressable_sizing>), the variant
// palette, and the min touch target. Put N of these in a `flex-row gap-2` and
// they split evenly; no per-card divergence.

import React from 'react';
import { Text } from 'react-native';
import type { LucideIcon } from 'lucide-react-native';
import AnimatedPressable from '@/components/AnimatedPressable';
import { useThemeColors } from '@/lib/useThemeColors';
import { isWeb } from '@/lib/platform';

export type CardActionVariant = 'neutral' | 'primary' | 'success' | 'destructive';

interface CardActionButtonProps {
  label: string;
  onPress: (e?: { stopPropagation: () => void }) => void;
  icon?: LucideIcon;
  variant?: CardActionVariant;
  accessibilityLabel?: string;
  /** Stop the press from bubbling to a parent card Pressable (grid cards). */
  stopPropagation?: boolean;
  /**
   * Refuses the press and dims the button. Use for an action with a server
   * round-trip in flight: `request-access` writes a row AND moves the stage, so
   * a second tap while the first is running is a second request the user has no
   * feedback about. (The upsert is idempotent, so this is about honesty, not
   * corruption.)
   */
  disabled?: boolean;
}

export function CardActionButton({
  label,
  onPress,
  icon: Icon,
  variant = 'neutral',
  accessibilityLabel,
  stopPropagation = false,
  disabled = false,
}: CardActionButtonProps) {
  const colors = useThemeColors();

  const palette: Record<CardActionVariant, { bg: string; fg: string }> = {
    neutral: { bg: colors.surface.secondary, fg: colors.text.tertiary },
    primary: { bg: colors.primary[600], fg: colors.text.inverse },
    success: { bg: colors.traffic.greenLight, fg: colors.success },
    destructive: { bg: colors.red.light, fg: colors.error },
  };
  const { bg, fg } = palette[variant];

  return (
    <AnimatedPressable
      onPress={(e) => {
        if (disabled) return;
        if (stopPropagation) e?.stopPropagation?.();
        onPress(e);
      }}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel ?? label}
      accessibilityState={{ disabled }}
      disabled={disabled}
      className="px-2 py-2.5 rounded-lg flex-row items-center justify-center gap-1.5"
      style={{
        backgroundColor: bg,
        minHeight: 44,
        flex: 1,
        // WHY flexBasis, and why 47%.
        //
        // The parent action row is `flex-row flex-wrap`, but `flex: 1` alone
        // carries `flexBasis: 0%` — every button's hypothetical main size is
        // then zero, the line never overflows, and the row therefore NEVER
        // WRAPS. Four actions were squeezed onto one line instead, which is why
        // "Request access" rendered as "Request..." on a 393pt screen.
        //
        // 47% makes two buttons per line the natural fit on mobile and lets the
        // rest wrap, so labels keep their width. This mirrors `ModalAction`,
        // which solved the same problem for modal action rows — same value, so
        // the two action-row styles stay recognisably one system.
        flexBasis: isWeb ? 'auto' : '47%',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      {Icon ? <Icon size={16} color={fg} /> : null}
      <Text numberOfLines={1} style={{ color: fg }} className="text-sm font-medium">
        {label}
      </Text>
    </AnimatedPressable>
  );
}
